import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, User, Smartphone, X, RefreshCw } from "lucide-react";
import { LogoWatermark } from "../landing/HeroSections";
import { Footer } from "../landing/InteractiveSections";
import { LOGO_URL } from "../../constants";

/**
 * Login surface for the customer portal — extracted from CustomerPortal.js.
 * Renders the three flows (social picker, phone entry, OTP) driven entirely by props.
 */
export const CustomerLoginScreen = ({
  authError, setAuthError,
  authMode, setAuthMode,
  getGoogleAuthUrl, handleFacebookLogin,
  phoneInput, setPhoneInput,
  otpCode, setOtpCode, otpPhone,
  otpSending, otpVerifying, otpError,
  otpCooldown,
  handleSendOtp, handleResendOtp, handleVerifyOtp,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFAF5] to-[#FFF0E8] relative overflow-hidden flex flex-col">
      <LogoWatermark />
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#E83E8C]/10 px-4 py-3 relative z-10">
        <div className="max-w-xl mx-auto flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft size={18} className="text-[#475569]" />
            <img src={LOGO_URL} alt="DoozyGo" className="h-8" />
          </Link>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center relative z-10 px-4 py-8">
        <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-[#f0e6d9] p-8 text-center">
          <div className="w-16 h-16 bg-[#E83E8C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <User size={32} className="text-[#E83E8C]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1E1B4B] mb-1">My Account</h1>
          <p className="text-sm text-[#475569] mb-6">Sign in to manage bookings, earn balloons &amp; unlock rewards!</p>

          {authError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="auth-error">
              {authError}
              <button onClick={() => setAuthError("")} className="ml-2 text-red-400 hover:text-red-600"><X size={14} /></button>
            </div>
          )}

          {authMode === "select" && (
            <div className="space-y-3">
              <a href={getGoogleAuthUrl()} className="flex items-center justify-center gap-3 w-full px-5 py-3 rounded-xl bg-white border-2 border-gray-200 hover:border-[#4285F4] hover:shadow-md transition-all text-[#1E1B4B] font-medium" data-testid="google-login-btn">
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </a>
              {process.env.REACT_APP_FACEBOOK_LOGIN_ENABLED === "true" && (
                <button onClick={handleFacebookLogin} className="flex items-center justify-center gap-3 w-full px-5 py-3 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium transition-all hover:shadow-md" data-testid="facebook-login-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Continue with Facebook
                </button>
              )}
              <button onClick={() => { setAuthMode("phone"); setAuthError(""); }} className="flex items-center justify-center gap-3 w-full px-5 py-3 rounded-xl bg-white border-2 border-gray-200 hover:border-[#E83E8C] hover:shadow-md transition-all text-[#1E1B4B] font-medium" data-testid="phone-login-btn">
                <Smartphone size={20} className="text-[#E83E8C]" /> Continue with Mobile Number
              </button>
              <p className="text-[10px] text-[#94a3b8] mt-4">
                By signing in, you agree to our <Link to="/terms-of-service" className="underline">Terms</Link> &amp; <Link to="/privacy-policy" className="underline">Privacy Policy</Link>
              </p>
            </div>
          )}

          {authMode === "phone" && (
            <div className="space-y-4">
              <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-[#E83E8C] transition-colors">
                <span className="px-3 py-3 bg-gray-50 text-sm text-[#475569] font-medium border-r">+91</span>
                <input
                  type="tel" value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="Enter mobile number"
                  className="flex-1 px-3 py-3 text-sm outline-none" autoFocus
                  data-testid="phone-number-input"
                />
              </div>
              {otpError && <p className="text-xs text-red-500">{otpError}</p>}
              <button onClick={handleSendOtp} disabled={otpSending || phoneInput.length < 10}
                className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-semibold disabled:opacity-50 hover:bg-[#d1356f] transition-colors"
                data-testid="send-otp-btn">
                {otpSending ? "Sending OTP..." : "Send OTP"}
              </button>
              <button onClick={() => { setAuthMode("select"); }} className="text-sm text-[#475569] hover:text-[#E83E8C]">Back to all options</button>
            </div>
          )}

          {authMode === "otp" && (
            <div className="space-y-4">
              <p className="text-sm text-[#475569]">OTP sent to <span className="font-semibold text-[#1E1B4B]">+91 {otpPhone}</span></p>
              <input
                type="text" inputMode="numeric" value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="w-full px-4 py-3 text-center text-lg tracking-[0.3em] font-mono border-2 border-gray-200 rounded-xl focus:border-[#E83E8C] outline-none transition-colors"
                autoFocus data-testid="otp-code-input"
              />
              {otpError && <p className="text-xs text-red-500">{otpError}</p>}
              <button onClick={handleVerifyOtp} disabled={otpVerifying || otpCode.length < 4}
                className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-semibold disabled:opacity-50 hover:bg-[#d1356f] transition-colors"
                data-testid="verify-otp-btn">
                {otpVerifying ? "Verifying..." : "Verify & Login"}
              </button>
              <div className="flex items-center justify-center gap-4 text-sm">
                <button onClick={handleResendOtp} disabled={otpCooldown > 0 || otpSending}
                  className={`flex items-center gap-1 ${otpCooldown > 0 ? "text-[#94a3b8]" : "text-[#E83E8C] hover:underline"}`}
                  data-testid="resend-otp-btn">
                  <RefreshCw size={12} />{otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Resend OTP"}
                </button>
                <span className="text-gray-300">|</span>
                <button onClick={() => { setAuthMode("phone"); setOtpCode(""); }} className="text-[#475569] hover:text-[#E83E8C]">Change number</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CustomerLoginScreen;
